import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Filters, FilterState } from '../../../../filters-toolbar/filters-toolbar.model';
import { FiltersService } from '../../../../filters-toolbar/filters.service';
import { FloorClass } from '../../../../models/floor.class';
import { AbstractSpaceClass } from '../../../../models/space/abstract-space.class';
import { AvailabilitySpaceClass } from '../../../../models/space/availability-space.class';
import { NonRentableSpaceClass } from '../../../../models/space/non-rentable-space.class';
import { TenantSpaceClass } from '../../../../models/space/tenant-space.class';
import { UnaccountedSpaceClass } from '../../../../models/space/unaccounted-space.class';
import {
  getUserRatePrefs,
  isNotNullOrEmptyString,
  processRateOpts,
  sanitizeValueOrNull,
} from '../../../../shared/utils/functions';
import { MappedLov } from '../../../../stacking-plan.model';
import { StackingPlanService } from '../../../../stacking-plan.service';
import { Settings, SettingsLeaseType, SpaceCardDetails } from '../../../actions-toolbar/settings/settings.model';
import { SettingsService } from '../../../actions-toolbar/settings/settings.service';
import { DetailsOverlayComponent } from './details-overlay/details-overlay.component';
import { AreaUrl, CurrencyUrl, SpaceClassificationUrl } from './details-overlay/details-overlay.constants';
import { SuiteType, UserSettingsPreference } from './item.model';
import { OverlayService } from './overlay.service';
import { L10nTranslationService } from 'angular-l10n';
import { SpLinkOpportunityService } from '../../../../shared/services/sp-link-opportunity.service';
import { DataObject } from '../../../../models/data-object.models';
import { MatDialog } from '@angular/material/dialog';
import { DetailsModalComponent } from './details-modal/details-modal.component';

const PROPERTY_TYPE_RETAIL = 'Retail';

interface ItemCardData {
  title: string;
  tag: string;
  spaceSize: string;
  suiteNumber: string;
  // If leaseEndingDate should be hidden then remove it from the object, else either the data will be shown or N/A will be presented.
  leaseEndingDate?: string;
  rentDate: string;
  leaseRate: string;
  tenantRepAgency: string;
  isOwnerOccupied?: boolean;
}

const EMPTY_CARD: ItemCardData = {
  title: null,
  tag: null,
  spaceSize: null,
  suiteNumber: null,
  leaseEndingDate: null,
  rentDate: null,
  tenantRepAgency: null,
  leaseRate: null,
};

const MAX_WIDTH = 100;
const MIN_WIDTH = 3;
@UntilDestroy()
@Component({
  selector: 'sp-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  standalone: false,
})
export class ItemComponent implements OnDestroy, OnInit {
  @HostBinding('style.minWidth') itemWidth: string = null;

  @Input() item: AbstractSpaceClass;
  @Input() totalNumberOfFloors: number;
  @Input() containerRecordFloors: DataObject[];
  @Input() floorData: FloorClass;
  @Input() selectedUsageType: string;

  public floorNumber: string;
  public itemClass: string;
  public isFilterOn: boolean;
  public isContentOn: boolean;
  public suiteTypeEnum = SuiteType;
  public settings: Settings;

  itemDataToDisplay: ItemCardData = EMPTY_CARD;
  private clickable = true;

  constructor(
    private filterService: FiltersService,
    private overlayService: OverlayService,
    private settingsService: SettingsService,
    private stackingPlanService: StackingPlanService,
    private translationService: L10nTranslationService,
    private spLinkOpportunityService: SpLinkOpportunityService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getSettings();
    this.initDataToDisplay();
    this.subscribeToLinkOpportunity();
    this.filterService.filterState$.pipe(untilDestroyed(this)).subscribe((filterState: FilterState) => {
      if (this.item && this.item instanceof TenantSpaceClass) {
        const { class: klass = '', id: filterKey = 0 } = this.filterService.getItemSchemaClass(
          this.item,
          this.floorNumber
        );
        this.setItemClass(klass);
        this.setIsFilterOn(filterState.selectedFilters, filterKey);
      }
    });
  }

  ngOnDestroy(): void {
    /* required by untilDestroyed */
  }

  showDetailsOverlay(origin: HTMLElement): void {
    if (!this.clickable) {
      return;
    }
    const openInModal = this.item instanceof AvailabilitySpaceClass || this.item instanceof TenantSpaceClass;
    if (openInModal) {
      this.dialog.open(DetailsModalComponent, {
        width: '100%',
        autoFocus: false,
        panelClass: 'sp-details-item-modal',
        data: {
          space: this.item,
          containerRecordFloors: this.containerRecordFloors,
          progressbarClass: this.itemClass,
          unaccountedSpace: this.floorData.floorArea - this.floorData.getFloorSpaceAreaSum(),
        },
      });
      return;
    }

    // !TODO SP: FIX OVERLAY
    this.overlayService.openOverlayBox<DetailsOverlayComponent>(
      DetailsOverlayComponent,
      {
        space: this.item,
        floor: this.floorNumber,
        containerRecordFloors: this.containerRecordFloors,
        unaccountedSpace: this.floorData.floorArea - this.floorData.getFloorSpaceAreaSum(),
      },
      origin,
      this.itemClass,
      { floorNo: this.floorNumber, totalNumberOfFloors: this.totalNumberOfFloors }
    );
  }

  private initDataToDisplay() {
    this.floorNumber = this.floorData.floorNumber ? this.floorData.floorNumber : this.floorData.floorIndex.toString();
    const uomLov$ = this.getUomLov();
    uomLov$.pipe(map((uomLov: [MappedLov, MappedLov, MappedLov]) => this.getItemCardData(uomLov))).subscribe(result => {
      this.itemDataToDisplay = result;
    });
  }

  private getItemCardData(uomLov: [MappedLov, MappedLov, MappedLov]): ItemCardData {
    const [areaUomLov, , spaceClassificationUomLov] = uomLov;
    const userMonthlyYearly = getUserRatePrefs();
    if (this.item instanceof AvailabilitySpaceClass) {
      return this.getAvailabilityData(this.item, areaUomLov, userMonthlyYearly);
    }
    if (this.item instanceof TenantSpaceClass) {
      return this.getTenantData(this.item, areaUomLov, userMonthlyYearly);
    }
    if (this.item instanceof NonRentableSpaceClass) {
      return this.getNonRentableData(this.item, { areaUomLov, spaceClassificationUomLov });
    }
    if (this.item instanceof UnaccountedSpaceClass) {
      return this.getUnaccountedData(this.item, areaUomLov);
    }
    this.clickable = false;
    return this.getUnknownData();
  }

  private getUomLov(): Observable<[MappedLov, MappedLov, MappedLov]> {
    const areaUomLov$ = this.stackingPlanService.getLovValuesMap(AreaUrl);
    const currencyUomLov$ = this.stackingPlanService.getLovValuesMap(CurrencyUrl);
    const spaceClassificationUomLov$ = this.stackingPlanService.getLovValuesMap(SpaceClassificationUrl);
    return forkJoin([areaUomLov$, currencyUomLov$, spaceClassificationUomLov$]);
  }

  private getAvailabilityData(
    space: AvailabilitySpaceClass,
    areaUomLov: MappedLov,
    userSettingsRents: string
  ): ItemCardData {
    const { suiteNumber } = space;
    const suite = this.translationService.translate('actionsToolbar.suite');
    const title = isNotNullOrEmptyString(suiteNumber) ? `${suite} ${suiteNumber}` : null;
    const tag = space.isCurrentSpace() ? 'commonLabels.available' : null;
    const spaceSize = space.generateFloorSpace(areaUomLov);
    const rentDate = this.settings?.cardDetails.rentDate
      ? space.generateRentRate(userSettingsRents, this.settings?.cardDetails)
      : null;
    const tenantRepAgency = space.generateListingRepAgency();

    return {
      title,
      tag,
      spaceSize,
      suiteNumber,
      rentDate,
      tenantRepAgency,
      leaseRate: null,
    };
  }

  private getTenantData(
    space: TenantSpaceClass,
    areaUomLov: MappedLov,
    userSettingsRents: UserSettingsPreference
  ): ItemCardData {
    const { availabilitySpace, tenant, companyName } = space;
    const brandName = space.getBrandName();
    const isTenancyForRetail = tenant.ref_property_usage_type_desc === PROPERTY_TYPE_RETAIL;

    let tag = null;
    if (availabilitySpace != null) {
      // Criteria 1
      // Tenant is a future tenant (Start Date exists and is a future date)
      const isFutureTenant = space.isFutureSpace();
      // And the availability is future (Status is Active, the Date Available is future)
      const isFutureAvailability = availabilitySpace.isFutureSpace();

      // Criteria 2
      // there is an availability with Status is Active, and Date Available is past
      // @see (https://jira.cbre.com/browse/MIQ-25990)
      const isCurrentAvailability = availabilitySpace.isCurrentSpace();

      if ((isFutureTenant && isFutureAvailability) || isCurrentAvailability) {
        tag = 'commonLabels.available';
      }
      if (isFutureTenant && isCurrentAvailability) {
        tag = 'commonLabels.upcoming';
      }
    }

    const title = isTenancyForRetail && brandName != null ? brandName : companyName;
    const spaceSize = space.generateFloorSpace(areaUomLov);
    const suiteNumber = space.suiteNumber;
    const leaseEndingDate = space.getSpaceDates().endDate;
    const leaseRate = this.settings?.cardDetails.showLeaseRate
      ? space.generateItemRentRate(userSettingsRents, this.settings.cardDetails.leaseRateOpts as SettingsLeaseType)
      : null;
    const tenantRepAgency = space.generateTenantRepAgency();
    const rentDate = null;
    const isOwnerOccupied = space.lease.lease_record_type_desc === 'Owner Occupied';
    return {
      title,
      tag,
      spaceSize,
      suiteNumber,
      leaseEndingDate,
      leaseRate,
      tenantRepAgency,
      rentDate,
      isOwnerOccupied,
    };
  }

  private getNonRentableData(space: NonRentableSpaceClass, mappedUoms: { [key: string]: MappedLov }): ItemCardData {
    const { spaceClassificationTypeDesc } = space;
    const title = (
      mappedUoms.spaceClassificationUomLov[sanitizeValueOrNull(spaceClassificationTypeDesc)] || ''
    ).toString();
    space.spaceClassificationTitle = title;
    const tag = null;
    const spaceSize = space.generateFloorSpace(mappedUoms.areaUomLov);
    const suiteNumber = space.suiteNumber;
    const leaseEndingDate = null;
    const rentDate = null;
    const tenantRepAgency = null;
    const leaseRate = null;
    return {
      title,
      tag,
      spaceSize,
      suiteNumber,
      leaseEndingDate,
      rentDate,
      tenantRepAgency,
      leaseRate,
    };
  }

  private getUnaccountedData(space: UnaccountedSpaceClass, areaUomLov: MappedLov): ItemCardData {
    const tag = null;
    const spaceSize = space.generateFloorSpace(areaUomLov);
    const title = spaceSize.toString();
    const suiteNumber = space.suiteNumber;
    const leaseEndingDate = null;
    const rentDate = null;
    const tenantRepAgency = null;
    const leaseRate = null;
    return {
      title,
      tag,
      spaceSize,
      suiteNumber,
      leaseEndingDate,
      rentDate,
      tenantRepAgency,
      leaseRate,
    };
  }

  private getUnknownData(): ItemCardData {
    return EMPTY_CARD;
  }

  private setItemClass(klass: string) {
    this.itemClass = klass;
  }

  private setIsFilterOn(selectedFilters: Filters, filterKey: number) {
    this.isFilterOn = selectedFilters[filterKey];
  }

  private getSettings() {
    return this.settingsService.settings$
      .pipe(
        tap(() => {
          // update item width during settings setup
          this.setItemWidth();
        }),
        untilDestroyed(this),
        filter(Boolean),
        switchMap(settings => {
          this.isContentOn = Object.values(settings.cardDetails).some(detailFlag => detailFlag);
          // since BE may return "Default", extra processing for leaseRateOpts property is needed
          settings.cardDetails['leaseRateOpts'] = processRateOpts(settings.cardDetails['leaseRateOpts']);
          const leaseSelectionChanged =
            settings.cardDetails['leaseRateOpts'] !== this.settings?.cardDetails['leaseRateOpts'];
          const isAskingRentChanged =
            this.item instanceof AvailabilitySpaceClass &&
            this.isAskingRentChanged(this.settings?.cardDetails, settings?.cardDetails);
          this.settings = this.#processSettings(settings);
          if ((this.item instanceof TenantSpaceClass && leaseSelectionChanged) || isAskingRentChanged) {
            return this.getUomLov().pipe(
              map((uomLov: [MappedLov, MappedLov, MappedLov]) => this.getItemCardData(uomLov)),
              tap(results => {
                this.itemDataToDisplay = results;
              })
            );
          }

          return of(null);
        })
      )
      .subscribe();
  }

  private isAskingRentChanged(oldCardDetails: SpaceCardDetails, newCardDetails: SpaceCardDetails): boolean {
    return (
      oldCardDetails?.includeEstimatedRateIndicator !== newCardDetails?.includeEstimatedRateIndicator ||
      oldCardDetails?.includeEstimatedRates !== newCardDetails?.includeEstimatedRates
    );
  }

  #processSettings(settings: Settings): Settings {
    const cardDetails = { rentDate: null, showLeaseRate: null, leaseRateOpts: null };
    let processedCardSettings = Object.assign({}, { ...settings.cardDetails, ...cardDetails });
    if (this.item instanceof AvailabilitySpaceClass) {
      processedCardSettings = { ...processedCardSettings, rentDate: settings.cardDetails.rentDate };
    }
    if (this.item instanceof TenantSpaceClass) {
      processedCardSettings = {
        ...processedCardSettings,
        showLeaseRate: settings.cardDetails.showLeaseRate,
        leaseRateOpts: processRateOpts(settings.cardDetails['leaseRateOpts']),
      };
    }
    return { ...settings, cardDetails: processedCardSettings };
  }

  private setItemWidth() {
    this.itemWidth = this.getItemSize(this.item) + '%';
  }

  private getItemSize(item: AbstractSpaceClass): number {
    if (item instanceof UnaccountedSpaceClass) {
      return this.getUnaccountedItemSize();
    }
    const { floorArea } = this.floorData;
    const itemArea = item.getAreaSpace() || 0;
    if (!floorArea || !itemArea) {
      return MIN_WIDTH;
    }
    const itemSize = (MAX_WIDTH * itemArea) / floorArea;
    return Math.max(itemSize, MIN_WIDTH);
  }

  private getUnaccountedItemSize(): number {
    let totalSizeItems = 0;
    this.floorData.spaces
      .filter((space: AbstractSpaceClass) => !(space instanceof UnaccountedSpaceClass))
      .forEach((space: AbstractSpaceClass) => {
        const itemSize = this.getItemSize(space);
        totalSizeItems += itemSize;
        if (totalSizeItems >= MAX_WIDTH) {
          totalSizeItems = itemSize;
        }
      });

    const unaccountedSize = MAX_WIDTH - totalSizeItems;
    return unaccountedSize > MIN_WIDTH ? unaccountedSize : MIN_WIDTH;
  }

  private subscribeToLinkOpportunity() {
    this.spLinkOpportunityService.opportunityLinking$
      .pipe(
        untilDestroyed(this),
        filter(() => !!this.overlayService.overlayRef)
      )
      .subscribe(() => this.overlayService.disposeBox());
  }
}
